/**
 * Iter208.82 — Site Editing Context + primitive <Editable> e <Removable>.
 *
 * Attivabile via query `?edit=1` sul sito pubblico se l'utente loggato è il
 * proprietario. Salva override testi (debounced 700ms) e sezioni nascoste
 * su `PATCH /api/site/edits`.
 *
 * Uso nei template:
 *   <Editable field="hero.h1" as="h1">Case che raccontano una storia.</Editable>
 *   <Editable field="hero.p" multiline as="p">Selezioniamo…</Editable>
 *   <Removable sectionKey="hero"><section>…</section></Removable>
 */
import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import axios from "axios";
import { X } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SiteEditContext = createContext({
  editMode: false, edits: {}, hidden: new Set(),
  setEdit: () => {}, toggleHidden: () => {},
});

export function useSiteEdit() { return useContext(SiteEditContext); }

/**
 * Provider da usare in cima al sito pubblico (`PublicSite` wrapper).
 * Accetta `enable` (bool) — se false, edit-mode disabilitato indipendentemente
 * dai props.
 */
export function SiteEditProvider({ enable, initialEdits, initialHidden, token, children }) {
  const [editMode, setEditMode] = useState(!!enable);
  const [edits, setEdits] = useState(initialEdits || {});
  const [hidden, setHidden] = useState(new Set(initialHidden || []));
  const timers = useRef({});
  const flush = useCallback((field, value) => {
    if (!token) return;
    axios.patch(`${API}/site/edits`, { text_edits: { [field]: value } }, { headers: { Authorization: `Bearer ${token}` } })
      .catch(() => {});
  }, [token]);
  const setEdit = useCallback((field, value) => {
    setEdits((prev) => ({ ...prev, [field]: value }));
    clearTimeout(timers.current[field]);
    timers.current[field] = setTimeout(() => flush(field, value), 700);
  }, [flush]);
  const toggleHidden = useCallback((key) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      if (token) {
        axios.patch(`${API}/site/edits`, { hidden_sections: Array.from(next) }, { headers: { Authorization: `Bearer ${token}` } })
          .catch(() => {});
      }
      return next;
    });
  }, [token]);
  const value = useMemo(() => ({ editMode, edits, hidden, setEdit, toggleHidden, setEditMode }), [editMode, edits, hidden, setEdit, toggleHidden]);
  return <SiteEditContext.Provider value={value}>{children}</SiteEditContext.Provider>;
}

/**
 * <Editable field="hero.h1" as="h1">default</Editable>
 * — Mostra il testo. In edit-mode, click apre input/textarea, salva on-blur.
 */
export function Editable({ field, as = "span", multiline = false, className = "", children, style }) {
  const { editMode, edits, setEdit } = useSiteEdit();
  const Cmp = as;
  const defaultVal = typeof children === "string" ? children : (Array.isArray(children) ? children.map((c) => typeof c === "string" ? c : "").join("") : "");
  const current = edits[field] !== undefined ? edits[field] : defaultVal;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(current);
  useEffect(() => { setDraft(current); }, [current, editing]);

  if (!editMode) {
    return <Cmp className={className} style={style} data-testid={`editable-${field}`}>{current || children}</Cmp>;
  }
  if (!editing) {
    return (
      <Cmp
        className={className + " atrium-editable"}
        style={{ ...style, outline: "1px dashed rgba(110,140,124,0.5)", outlineOffset: "3px", cursor: "text", background: "rgba(255,251,235,0.35)" }}
        data-testid={`editable-${field}`}
        onClick={() => setEditing(true)}
        title="Clicca per modificare"
      >{current || children}</Cmp>
    );
  }
  const InputCmp = multiline ? "textarea" : "input";
  return (
    <InputCmp
      className={className + " atrium-editable-input"}
      style={{ ...style, background: "#FFFBEB", border: "1px dashed #A9744F", outline: "none", padding: "2px 6px", fontFamily: "inherit", fontSize: "inherit", fontWeight: "inherit", color: "inherit", width: multiline ? "100%" : undefined, minWidth: "120px", resize: multiline ? "vertical" : undefined, minHeight: multiline ? "3em" : undefined }}
      value={draft}
      autoFocus
      data-testid={`editable-input-${field}`}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => { setEditing(false); if (draft !== current) setEdit(field, draft); }}
      onKeyDown={(e) => { if (!multiline && e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") { setDraft(current); setEditing(false); } }}
      rows={multiline ? 3 : undefined}
    />
  );
}

/**
 * <Removable sectionKey="hero">…</Removable>
 * — Se `sectionKey` è nella lista `hidden_sections`: non renderizza.
 * — In edit-mode: aggiunge un tasto X in top-right per toglierla.
 */
export function Removable({ sectionKey, children, className = "" }) {
  const { editMode, hidden, toggleHidden } = useSiteEdit();
  if (hidden.has(sectionKey)) {
    if (!editMode) return null;
    return (
      <div style={{ padding: "16px 20px", background: "rgba(230,225,215,0.5)", border: "1px dashed #B99C7C", borderRadius: 8, margin: "16px 0", textAlign: "center", fontFamily: "sans-serif", fontSize: 13, color: "#7C7360" }}>
        Sezione "<b>{sectionKey}</b>" nascosta.{" "}
        <button
          type="button"
          onClick={() => toggleHidden(sectionKey)}
          data-testid={`restore-${sectionKey}`}
          style={{ marginLeft: 8, textDecoration: "underline", background: "none", border: "none", color: "#A9744F", cursor: "pointer" }}
        >Ripristina</button>
      </div>
    );
  }
  if (!editMode) return <>{children}</>;
  return (
    <div style={{ position: "relative" }} className={className} data-testid={`removable-${sectionKey}`}>
      {children}
      <button
        type="button"
        onClick={() => toggleHidden(sectionKey)}
        title="Nascondi questa sezione"
        aria-label={`Rimuovi ${sectionKey}`}
        data-testid={`remove-${sectionKey}`}
        style={{ position: "absolute", top: 12, right: 12, zIndex: 20, width: 32, height: 32, borderRadius: "50%", background: "#B5502D", color: "#fff", border: "2px solid #fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "0 6px 14px -6px rgba(0,0,0,0.4)" }}
      ><X size={14} weight="bold"/></button>
    </div>
  );
}

/**
 * Toolbar sticky top per attivare/disattivare edit mode.
 * Il pro proprietario che visita `?edit=1` la vede in cima al sito.
 */
export function SiteEditToolbar() {
  const { editMode, setEditMode, edits, hidden } = useSiteEdit();
  if (!editMode && !window.location.search.includes("edit=1")) return null;
  const count = Object.keys(edits || {}).length + (hidden?.size || 0);
  return (
    <div
      data-testid="site-edit-toolbar"
      style={{ position: "sticky", top: 0, zIndex: 200, background: "#0B1F2A", color: "#fff", padding: "8px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", fontFamily: "'Manrope',sans-serif", fontSize: 13 }}
    >
      <span>✎ Modalità modifica — clicca sui testi per cambiarli, X per nascondere sezioni. {count > 0 && <span style={{ opacity: 0.7 }}>({count} modifiche salvate)</span>}</span>
      <button
        type="button"
        onClick={() => setEditMode(!editMode)}
        data-testid="toggle-edit-mode"
        style={{ background: editMode ? "#1FAE8B" : "#374151", color: "#fff", padding: "6px 14px", borderRadius: 100, border: "none", cursor: "pointer", fontWeight: 600, fontSize: 12 }}
      >{editMode ? "Modifica ON" : "Attiva modifica"}</button>
    </div>
  );
}
